import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, PencilSimple, Trash, MapPin, Buildings, Cube, FloppyDisk, X, Image as ImageIcon,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import api from "@/lib/api";
import { eur, price, eurCompact, num } from "@/lib/format";
import { PageHeader, StatusBadge, Field } from "@/components/common";
import { placeImage } from "@/lib/placeImage";
import PlacesMap from "@/components/PlacesMap";
import NearbyPoi from "@/components/NearbyPoi";
import LocationResolver from "@/components/LocationResolver";
import FileUpload from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { localizedField } from "@/lib/localized";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const POI_FIELDS = ["airports", "highways", "universities", "hospitals", "tourism", "commercial", "transport"];

export default function PlaceProfile() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [imgUrl, setImgUrl] = useState("");
  const [highlight, setHighlight] = useState(null);
  const mapRef = useRef(null);

  const { data: place, isLoading } = useQuery({ queryKey: ["place", id], queryFn: async () => (await api.get(`/places/${id}`)).data });

  useEffect(() => {
    if (place) setForm({
      name: place.name || "", city: place.city || "", region: place.region || "", country: place.country || "",
      lat: place.lat || "", lng: place.lng || "", description: place.description || "", notes: place.notes || "",
      poi: { ...(place.poi || {}) },
    });
  }, [place]);

  const update = useMutation({
    mutationFn: async (payload) => (await api.put(`/places/${id}`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["place", id] }); qc.invalidateQueries({ queryKey: ["places"] }); toast.success(t("places.save", "Save")); },
    onError: () => toast.error(t("actionFailed", "Action failed")),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/places/${id}`)).data,
    onSuccess: () => { toast.success(t("places.deletePlace", "Delete place")); navigate("/places"); },
  });

  const focusOnMap = (item) => {
    setHighlight({ lat: item.lat, lng: item.lng, name: item.name });
    if (mapRef.current) mapRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (isLoading || !place)
    return <div className="flex justify-center py-40"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>;

  const props = place.properties || [];
  const saveProfile = () => { update.mutate(form); setEdit(false); };
  const setHeroFromUpload = (data) => update.mutate({ hero_file_id: data.id, hero_image_url: null });
  const setHeroFromUrl = () => { if (imgUrl.trim()) { update.mutate({ hero_image_url: imgUrl.trim(), hero_file_id: null }); setImgUrl(""); } };
  const removeHero = () => update.mutate({ hero_file_id: null, hero_image_url: null });

  return (
    <div>
      <button onClick={() => navigate("/places")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2545] mb-4" data-testid="place-back-btn">
        <ArrowLeft size={16} /> {t("places.title", "Places")}
      </button>

      <PageHeader
        title={place.name}
        subtitle={[place.region, place.country].filter(Boolean).join(", ") || t("places.profile", "Place Profile")}
        actions={
          <div className="flex gap-2">
            {edit ? (
              <>
                <Button onClick={saveProfile} data-testid="place-save-btn" className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white"><FloppyDisk size={16} className="mr-1.5" />{t("places.save", "Save")}</Button>
                <Button variant="outline" onClick={() => setEdit(false)} data-testid="place-cancel-btn" className="rounded-sm"><X size={16} /></Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setEdit(true)} data-testid="place-edit-btn" className="rounded-sm"><PencilSimple size={16} className="mr-1.5" />{t("places.edit", "Edit Place Profile")}</Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="place-delete-btn" className="rounded-sm text-red-600 hover:text-red-700"><Trash size={16} /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white rounded-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("places.deletePlace", "Delete place")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("places.deleteConfirm", "Delete this place profile? Properties are not affected.")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">{t("places.cancel", "Cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove.mutate()} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="place-confirm-delete">{t("places.deletePlace", "Delete place")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hero + image controls */}
        <div className="lg:col-span-2 space-y-4">
          <div className="relative h-[320px] rounded-sm overflow-hidden bg-slate-100 border border-slate-200" data-testid="place-hero">
            <img src={placeImage(place)} alt={place.name} className="w-full h-full object-cover" />
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{t("places.heroImage", "Hero image")}</div>
            <FileUpload category="place" label={t("places.uploadHero", "Upload / replace image")} accept="image/*" onUploaded={setHeroFromUpload} />
            <div className="flex gap-2 mt-3">
              <Input data-testid="place-image-url" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder={t("places.imageUrl", "Or paste image URL")} className="rounded-sm" />
              <Button variant="outline" onClick={setHeroFromUrl} data-testid="place-set-url" className="rounded-sm"><ImageIcon size={16} className="mr-1.5" />{t("places.changeImage", "Change image")}</Button>
            </div>
            {(place.hero_file_id || place.hero_image_url) && (
              <button onClick={removeHero} data-testid="place-remove-image" className="text-xs text-red-600 hover:underline mt-2">{t("places.removeHero", "Remove image")}</button>
            )}
          </div>

          {/* Description / details */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-serif text-xl mb-3">{t("places.description", "Description")}</h3>
            {edit ? (
              <Textarea data-testid="place-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" rows={4} />
            ) : (
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">{place.description || "—"}</p>
            )}
            <h3 className="font-serif text-xl mt-6 mb-3">{t("places.notes", "Notes")}</h3>
            {edit ? (
              <Textarea data-testid="place-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" rows={3} />
            ) : (
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">{place.notes || "—"}</p>
            )}
          </div>

          {/* POI / access */}
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="place-nearby-poi">
            {edit ? (
              <>
                <h3 className="font-serif text-xl mb-4">{t("places.nearbyPoi", "Nearby points of interest")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {POI_FIELDS.map((k) => (
                    <div key={k}>
                      <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">{t(`places.${k}`, k)}</div>
                      <Input data-testid={`place-poi-${k}`} value={form.poi?.[k] || ""} onChange={(e) => setForm({ ...form, poi: { ...form.poi, [k]: e.target.value } })} className="rounded-sm" />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <NearbyPoi lat={place.lat} lng={place.lng} onLocate={focusOnMap} />
            )}
          </div>
        </div>

        {/* Stats sidebar */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("places.properties", "Properties")}>{num(place.property_count)}</Field>
              <Field label={t("places.portfolioValue", "Portfolio value")}>{eurCompact(place.portfolio_value)}</Field>
              <Field label={t("places.avgPrice", "Average asking price")}>{eur(place.avg_price)}</Field>
              <Field label={t("places.demand", "Buyer / investor demand")}>{place.demand_buyers} / {place.demand_investors}</Field>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">{t("places.available", "Available")} {place.status_counts?.Available || 0}</span>
              <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">{t("places.reserved", "Reserved")} {place.status_counts?.Reserved || 0}</span>
              <span className="text-xs px-2 py-0.5 rounded-sm bg-blue-50 text-blue-700">{t("places.underNegotiation", "Under negotiation")} {place.status_counts?.["Under negotiation"] || 0}</span>
              <span className="text-xs px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600">{t("places.sold", "Sold")} {place.status_counts?.Sold || 0}</span>
            </div>
          </div>

          {edit && (
            <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
              <div className="text-xs uppercase tracking-wide text-slate-400">{t("places.searchLocation", "Search location")}</div>
              <LocationResolver
                initial={place.lat && place.lng ? { lat: place.lat, lng: place.lng, city: form.city, region: form.region, country: form.country } : null}
                onConfirm={(pt) => setForm((f) => ({ ...f, lat: pt.lat, lng: pt.lng, city: pt.city || f.city, region: pt.region || f.region, country: pt.country || f.country }))}
              />
              <div><div className="text-xs uppercase tracking-wide text-slate-400 mb-1">{t("places.placeName", "Place name")}</div><Input data-testid="place-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><div className="text-xs text-slate-400 mb-1">{t("places.city", "City")}</div><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm" /></div>
                <div><div className="text-xs text-slate-400 mb-1">{t("places.region", "Region")}</div><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="rounded-sm" /></div>
                <div><div className="text-xs text-slate-400 mb-1">{t("places.country", "Country")}</div><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><div className="text-xs text-slate-400 mb-1">{t("places.lat", "Latitude")}</div><Input type="number" value={form.lat} onChange={(e) => setForm({ ...form, lat: parseFloat(e.target.value) })} className="rounded-sm" /></div>
                <div><div className="text-xs text-slate-400 mb-1">{t("places.lng", "Longitude")}</div><Input type="number" value={form.lng} onChange={(e) => setForm({ ...form, lng: parseFloat(e.target.value) })} className="rounded-sm" /></div>
              </div>
            </div>
          )}

          {place.lat && place.lng && (
            <div ref={mapRef}>
              <PlacesMap places={[place]} focus={place} highlight={highlight} height="240px" onOpenProfile={() => {}} onViewProperties={() => {}} />
            </div>
          )}
        </div>
      </div>

      {/* Linked properties */}
      <div className="mt-8">
        <h3 className="font-serif text-2xl mb-4">{t("places.linkedProperties", "Linked properties")} ({props.length})</h3>
        {props.length === 0 ? (
          <p className="text-slate-400 text-sm">—</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {props.map((p) => (
              <div key={p.id} data-testid={`place-property-${p.id}`} onClick={() => navigate(`/properties/${p.id}`)}
                className="group bg-white border border-slate-200 rounded-sm overflow-hidden cursor-pointer hover:-translate-y-[3px] hover:shadow-[0_12px_40px_rgba(11,37,69,0.1)] transition-transform">
                <div className="relative h-40 overflow-hidden bg-slate-100">
                  {p.cover_image ? <img src={p.cover_image} alt={localizedField(p, "title", i18n)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full flex items-center justify-center text-slate-300"><Buildings size={36} /></div>}
                  <div className="absolute top-3 left-3"><StatusBadge status={p.status} /></div>
                  {(p.external_3d_url || p.category === "Development project") && (
                    <div className="absolute top-3 right-3 bg-[#C5A059] text-white text-[10px] px-2 py-0.5 rounded-sm flex items-center gap-1"><Cube size={12} />3D</div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-xs text-slate-400">{p.reference} · {p.category}</div>
                  <h4 className="font-serif text-base text-slate-900 mt-1 line-clamp-1">{localizedField(p, "title", i18n)}</h4>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-1"><MapPin size={12} /> {p.city}</div>
                  <div className="font-serif text-lg text-[#0B2545] mt-2">{price(p.asking_price)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
